import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { ArrowUpDown, ChevronUp, ChevronDown, ChevronRight, MapPin } from 'lucide-react'
import { TablePagination } from 'components/ui/pagination'
import MapLocationPopupCN from 'components/popups/MapLocationPopupCN'
import LocationActionSheet from './LocationActionSheet'
import useIsMobile from 'hooks/useIsMobile'
import usePartnerProfileDialog from 'features/Partners/hooks/usePartnerProfileDialog'

const sortFields = [
	{ id: 'assetName', label: 'Location' },
	{ id: 'address', label: 'Address' },
	{ id: 'city', label: 'City' },
	{ id: 'stateProvince', label: 'State' },
	{ id: 'postalCode', label: 'Postal Code' },
]

const getNestedValue = (obj, path) => {
	return path.split('.').reduce((acc, key) => acc?.[key], obj)
}

const LocationsTableTW = ({ locations, openInMap, recordCount, userId }) => {
	const isMobile = useIsMobile()
	const [page, setPage] = useState(0)
	const [rowsPerPage, setRowsPerPage] = useState(10)
	const [orderBy, setOrderBy] = useState(null)
	const [order, setOrder] = useState('asc')

	// Sheet state
	const [sheetOpen, setSheetOpen] = useState(false)
	const [selectedLocation, setSelectedLocation] = useState(null)

	// The owner avatar opens the shared partner profile popup (MYD-535): the
	// same one every partner list opens, rather than a hand-rolled overlay.
	const { openPartner, partnerDialog } = usePartnerProfileDialog()

	// Map popup state
	const [openMap, setOpenMap] = useState(false)
	const [mapLocation, setMapLocation] = useState(null)

	const handleThreeDotClick = (event, location) => {
		event.stopPropagation()
		setSelectedLocation(location)
		setSheetOpen(true)
	}

	const handleAvatarClick = (event, record) => {
		event.stopPropagation()
		openPartner(record?.owner?._id, record?.owner)
	}

	const handleOpenMap = (e, location) => {
		e.stopPropagation()
		setMapLocation(location)
		setOpenMap(true)
	}

	const handleSort = fieldId => {
		if (orderBy === fieldId) {
			setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setOrderBy(fieldId)
			setOrder('asc')
		}
		setPage(0)
	}

	const sortedLocations = useMemo(() => {
		if (!orderBy) return locations

		return [...locations].sort((a, b) => {
			const aVal = (getNestedValue(a, orderBy) || '').toString().toLowerCase()
			const bVal = (getNestedValue(b, orderBy) || '').toString().toLowerCase()

			if (aVal < bVal) return order === 'asc' ? -1 : 1
			if (aVal > bVal) return order === 'asc' ? 1 : -1
			return 0
		})
	}, [locations, orderBy, order])

	const paginatedLocations = useMemo(() => {
		// On a phone the list is unpaginated and scrolled, so every row renders.
		// Hiding the pager without this would strand every record past the tenth.
		if (isMobile) return sortedLocations
		const start = page * rowsPerPage
		return sortedLocations.slice(start, start + rowsPerPage)
	}, [sortedLocations, page, rowsPerPage, isMobile])

	const SortIcon = ({ fieldId }) => {
		if (orderBy !== fieldId) return <ArrowUpDown className="h-3 w-3 text-gray-300" />
		return order === 'asc' ? (
			<ChevronUp className="h-3 w-3 text-[#0080fe]" />
		) : (
			<ChevronDown className="h-3 w-3 text-[#0080fe]" />
		)
	}

	return (
		<>
			<div className="rounded-lg border border-gray-200 overflow-hidden shadow-sm bg-white p-4">
				{/* Sort pills. Hidden on mobile: five pills wrap onto two lines at
				    390px, costing most of the first screenful before a single record
				    appears. Mobile only, deliberately — this list renders flex rows
				    rather than a real table, so there is no column header behind
				    these and hiding them everywhere would strip sorting from the
				    desktop List view entirely. The sort state itself is untouched. */}
				{!isMobile && (
				<div className="mb-3 flex items-center gap-1.5 flex-wrap">
					<span className="text-xs text-gray-400 mr-1">Sort by:</span>
					{sortFields.map(field => (
						<button
							key={field.id}
							type="button"
							onClick={() => handleSort(field.id)}
							className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
								orderBy === field.id ? 'bg-[#0080fe]/10 text-[#0080fe]' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
							}`}
						>
							{field.label}
							<SortIcon fieldId={field.id} />
						</button>
					))}
				</div>
				)}

				{/* List items */}
				<div className="divide-y divide-gray-100">
					{paginatedLocations.map(location => (
						<div key={location._id} className="flex items-center gap-3 py-2 group">
							{/* Avatar */}
							<Avatar className="h-8 w-8 flex-shrink-0">
								<AvatarImage src={location.image} />
								<AvatarFallback className="text-[10px] bg-gray-100 text-gray-400">
									{location.assetName?.[0] || '?'}
								</AvatarFallback>
							</Avatar>

						{isMobile ? (
								/*
								 * Stacked on mobile. The desktop row below puts name, address,
								 * city, state and postal code on ONE flex line; at 390px there
								 * is not enough width and `truncate min-w-0` collapses the name
								 * and city to zero, leaving a postal code with no indication of
								 * which location it belongs to.
								 *
								 * The address keeps its tap-to-open-map behaviour, so the second
								 * line is a button with the locality rendered beside it rather
								 * than one plain string.
								 */
								<div className="min-w-0 flex-1">
									<Link
										to={`/locations/${location._id}`}
										className="block text-sm font-medium text-primary hover:underline truncate"
									>
										{location.assetName}
									</Link>
									{/*
									  * One control for the whole locality line. Splitting the
									  * address from the city put the comma in the wrong place and
									  * left the pin stranded on its own line; and since the line
									  * describes where the location IS, opening the map from any
									  * part of it is the natural action. The name above remains
									  * the link to the location itself.
									  *
									  * Joined with filter(Boolean) rather than interpolated:
									  * several records have no city, and a template string would
									  * leave a stray comma.
									  */}
									<button
										type="button"
										onClick={e => handleOpenMap(e, location)}
										className="mt-0.5 flex items-center gap-1 min-w-0 max-w-full text-xs text-gray-500 hover:text-[#0080fe] transition-colors cursor-pointer"
									>
										<MapPin className="h-3 w-3 flex-shrink-0" />
										<span className="truncate">
											{[location.address, location.city, location.stateProvince]
												.filter(Boolean)
												.join(', ')}
										</span>
									</button>
								</div>
							) : (
								<>
									{/* Location name */}
									<Link
										to={`/locations/${location._id}`}
										className="text-sm font-medium text-primary hover:underline truncate min-w-0"
									>
										{location.assetName}
									</Link>

									{/* Address pill */}
									<button
										type="button"
										onClick={e => handleOpenMap(e, location)}
										className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs text-gray-600 hover:bg-[#0080fe]/10 hover:text-[#0080fe] hover:border-[#0080fe]/20 transition-colors cursor-pointer flex-shrink-0"
									>
										<MapPin className="h-3 w-3" />
										{location.address}
									</button>

									{/* City */}
									<span className="text-sm text-gray-500 truncate min-w-0">{location.city}</span>

									{/* State */}
									<span className="text-sm text-gray-500 flex-shrink-0">{location.stateProvince}</span>

									{/* Postal Code */}
									<span className="text-sm text-gray-500 flex-shrink-0">{location.postalCode}</span>
								</>
							)}

							{/* Edit — right-justified */}
							<div className={`ml-auto flex-shrink-0 transition-opacity ${isMobile ? '' : 'opacity-50 group-hover:opacity-100'}`}>
								{location.owner?._id === userId ? (
									<button
										type="button"
										className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors"
										onClick={e => handleThreeDotClick(e, location)}
									>
										<ChevronRight className="h-4 w-4" />
									</button>
								) : (
									<Avatar className="h-7 w-7 cursor-pointer" onClick={e => handleAvatarClick(e, location)}>
										<AvatarImage src={location.owner?.image} />
										<AvatarFallback />
									</Avatar>
								)}
							</div>
						</div>
					))}
				</div>

				{!isMobile && recordCount > 10 && (
					<TablePagination
						page={page}
						rowsPerPage={rowsPerPage}
						count={recordCount}
						onPageChange={setPage}
						onRowsPerPageChange={setRowsPerPage}
					/>
				)}
			</div>

			{/* Map popup */}
			{openMap && mapLocation && (
				<MapLocationPopupCN
					open={openMap}
					onClose={() => setOpenMap(false)}
					location={mapLocation}
				/>
			)}

			{/* Owner info modal */}
			{partnerDialog}

			<LocationActionSheet location={selectedLocation} open={sheetOpen} onClose={() => setSheetOpen(false)} />
		</>
	)
}

export default LocationsTableTW
